<script setup lang="ts">
import { useEventListener, useMutationObserver } from '@vueuse/core';
import { useTemplateRef, watch } from 'vue';
import { injectHoverCardOutletContext } from '#layers/ui/composables/hover-card-context';

/** Renders the card owned by `DHoverCardProvider`. Place it inside `DApp` so the content gets Nuxt UI's config. */

const { active, open, openReference, keepOpen, closeSoon, close } = injectHoverCardOutletContext();

// Reka reports opening from its own delayed timer, which can land after the pointer already left the
// card; the card's pointer handlers own that, so only dismissals (Escape, outside press) apply.
function onOpenChange(value: boolean): void {
	if (!value) close();
}

// Reka closes when a scroll moves the trigger, but it only knows triggers it renders itself.
useEventListener(() => openReference.value ? window : undefined, 'scroll', (event) => {
	const reference = openReference.value;
	if (reference && event.target instanceof Node && event.target.contains(reference)) close();
}, { capture: true, passive: true });

// Reka keeps the card out of the tab order, but only for the content present when it mounts;
// content swapped in from another trigger needs the same treatment.
const contentElement = useTemplateRef('content');

function removeFromTabOrder(container: HTMLElement): void {
	for (const element of container.querySelectorAll<HTMLElement>('*')) {
		if (element.tabIndex >= 0) element.tabIndex = -1;
	}
}

watch(contentElement, (element) => {
	if (element) removeFromTabOrder(element);
});

useMutationObserver(contentElement, () => {
	if (contentElement.value) removeFromTabOrder(contentElement.value);
}, { childList: true, subtree: true });

function ActiveContent() {
	return active.value?.renderContent();
}
</script>

<template>
	<DPopover
		v-if="active"
		mode="hover"
		:open
		:reference="active.reference"
		:content="active.content"
		:ui="{ content: 'p-0 data-[state=closed]:animate-none data-[state=open]:animate-none' }"
		@update:open="onOpenChange"
	>
		<template #content>
			<!-- Without a trigger element Reka has no grace area, so the card tracks the pointer itself. -->
			<div ref="content" :class="active.contentClass" data-testid="hover-card-content" @pointerenter="keepOpen" @pointerleave="closeSoon">
				<ActiveContent />
			</div>
		</template>
	</DPopover>
</template>
